import { useEffect, useState, useMemo, useRef } from "react";
import {
    Plus,
    Loader2,
    Maximize2,
    Radio,
    Battery,
    Fuel,
    MapPin,
    Map as MapIcon,
    List,
    Wrench,
    User,
    Target,
    X,
    Trash2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import FocusHeader from "@/components/FocusHeader";
import ExpandCard from "@/components/ExpandCard";
import FleetMap from "@/components/FleetMap";

const STATE_META = {
    "on-station": { bg: "#00E676", fg: "#050505", label: "ON STATION" },
    transiting: { bg: "#2979FF", fg: "#050505", label: "TRANSITING" },
    deployed: { bg: "#FFEA00", fg: "#050505", label: "DEPLOYED" },
    "in-maintenance": { bg: "#FF5500", fg: "#050505", label: "MAINTENANCE" },
    offline: { bg: "#333333", fg: "#A1A1AA", label: "OFFLINE" },
    "lost-comms": { bg: "#FF1744", fg: "#FFFFFF", label: "LOST COMMS" },
    decommissioned: { bg: "#52525B", fg: "#FFFFFF", label: "DECOMMISSIONED" },
};

const STATE_LIST = Object.keys(STATE_META);

function StatePill({ value, onClick }) {
    const s = STATE_META[value] || STATE_META["on-station"];
    return (
        <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center justify-center px-2 py-1 min-w-[110px] font-mono text-[10px] tracking-[0.16em] font-bold hover:opacity-90"
            style={{ backgroundColor: s.bg, color: s.fg }}
        >
            {s.label}
        </button>
    );
}

export default function Fleet() {
    const [vessels, setVessels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [view, setView] = useState("list"); // "list" | "map"
    const flatRef = useRef([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/fleet/vessels");
            setVessels(data);
        } catch (e) {
            toast.error("Failed to load fleet.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("fleet", load);

    useEffect(() => {
        load();
    }, []);

    const indexed = useMemo(
        () => vessels.map((v, i) => ({ ...v, index: i + 1 })),
        [vessels]
    );

    useEffect(() => {
        flatRef.current = indexed;
    }, [indexed]);

    useEffect(() => {
        const onExpand = (e) => {
            const n = Number(e.detail?.index);
            const target = flatRef.current.find((x) => x.index === n);
            if (!target) {
                toast.warning(`Vessel #${n} not found.`);
                return;
            }
            setExpanded(target);
        };
        const onCollapse = () => setExpanded(null);
        const onRefresh = () => load();
        window.addEventListener("virtus:expand-vessel", onExpand);
        window.addEventListener("virtus:collapse-vessel", onCollapse);
        window.addEventListener("virtus:vessels-refreshed", onRefresh);
        return () => {
            window.removeEventListener("virtus:expand-vessel", onExpand);
            window.removeEventListener("virtus:collapse-vessel", onCollapse);
            window.removeEventListener("virtus:vessels-refreshed", onRefresh);
        };
    }, []);

    const save = async (data) => {
        try {
            if (editing?.id) {
                const { data: u } = await api.patch(`/fleet/vessels/${editing.id}`, data);
                setVessels((prev) => prev.map((x) => (x.id === u.id ? u : x)));
            } else {
                const { data: c } = await api.post("/fleet/vessels", data);
                setVessels((prev) => [...prev, c]);
            }
            setShowModal(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        try {
            await api.delete(`/fleet/vessels/${editing.id}`);
            setVessels((prev) => prev.filter((x) => x.id !== editing.id));
            setShowModal(false);
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const cycleState = async (v) => {
        const next = STATE_LIST[(STATE_LIST.indexOf(v.state) + 1) % STATE_LIST.length];
        try {
            const { data: u } = await api.patch(`/fleet/vessels/${v.id}`, { state: next });
            setVessels((prev) => prev.map((x) => (x.id === u.id ? u : x)));
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    const total = vessels.length;
    const onStation = vessels.filter((v) => v.state === "on-station").length;
    const deployed = vessels.filter((v) => v.state === "deployed" || v.state === "transiting").length;
    const down = vessels.filter((v) =>
        ["offline", "lost-comms", "in-maintenance"].includes(v.state)
    ).length;

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="AO/06"
                title="Fleet"
                subtitle="Autonomous vessels · real-time state & health."
            />

            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-virtus-line flex-shrink-0">
                <Metric label="TOTAL" value={total} color="#FFFFFF" />
                <Metric label="ON STATION" value={onStation} color="#00E676" />
                <Metric label="DEPLOYED" value={deployed} color="#FFEA00" />
                <Metric label="DOWN" value={down} color="#FF1744" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim truncate">
                    VESSELS
                    <span className="hidden md:inline"> // SAY <span className="text-virtus-orange">"EXPAND VESSEL N"</span> · <span className="text-virtus-orange">"STATUS VESSEL N"</span></span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex border border-virtus-line">
                        <button
                            onClick={() => setView("list")}
                            className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 ${
                                view === "list"
                                    ? "bg-virtus-orange text-black"
                                    : "text-virtus-dim hover:text-white"
                            }`}
                            data-testid="fleet-view-list-btn"
                        >
                            <List size={11} /> LIST
                        </button>
                        <button
                            onClick={() => setView("map")}
                            className={`flex items-center gap-1.5 font-mono text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border-l border-virtus-line ${
                                view === "map"
                                    ? "bg-virtus-orange text-black"
                                    : "text-virtus-dim hover:text-white"
                            }`}
                            data-testid="fleet-view-map-btn"
                        >
                            <MapIcon size={11} /> MAP
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            setEditing(null);
                            setShowModal(true);
                        }}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                        data-testid={TID.fleetAddBtn}
                    >
                        <Plus size={11} /> ADD VESSEL
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden relative">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim h-full">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : indexed.length === 0 ? (
                    <div className="p-4 h-full overflow-y-auto">
                        <EmptyState onAdd={() => setShowModal(true)} />
                    </div>
                ) : view === "map" ? (
                    <FleetMap
                        vessels={indexed}
                        stateMeta={STATE_META}
                        onSelect={(v) => setExpanded(v)}
                    />
                ) : (
                    <div className="h-full overflow-y-auto p-4" data-kiosk-scroll="true">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                            <AnimatePresence>
                                {indexed.map((v) => (
                                    <motion.div
                                        key={v.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        onClick={() => {
                                            setEditing(v);
                                            setShowModal(true);
                                        }}
                                        className="group relative border border-virtus-line bg-virtus-surface hover:border-virtus-orange p-4 cursor-pointer"
                                        style={{
                                            boxShadow: `inset 3px 0 0 ${
                                                (STATE_META[v.state] || STATE_META["on-station"]).bg
                                            }`,
                                        }}
                                        data-testid={TID.fleetVesselRow(v.id)}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <div className="font-mono text-[10px] tracking-[0.2em] font-bold text-virtus-orange">
                                                    #{String(v.index).padStart(2, "0")}
                                                </div>
                                                <div className="font-heading font-black text-xl uppercase tracking-tight">
                                                    {v.callsign}
                                                </div>
                                                {v.hull_number && (
                                                    <div className="font-mono text-[10px] tracking-[0.22em] text-virtus-faint">
                                                        · {v.hull_number}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpanded(v);
                                                }}
                                                className="opacity-0 group-hover:opacity-100 text-virtus-dim hover:text-virtus-orange"
                                                data-testid={`fleet-vessel-expand-${v.id}`}
                                                aria-label="Expand vessel"
                                            >
                                                <Maximize2 size={13} />
                                            </button>
                                        </div>
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                cycleState(v);
                                            }}
                                            className="mb-3"
                                        >
                                            <StatePill value={v.state} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 font-mono text-[10px] text-virtus-dim">
                                            <MetaRow icon={Battery} label="BAT" value={v.battery_percent != null ? `${v.battery_percent}%` : "—"} />
                                            <MetaRow icon={Fuel} label="FUEL" value={v.fuel_percent != null ? `${v.fuel_percent}%` : "—"} />
                                            <MetaRow icon={MapPin} label="LOC" value={v.location || "—"} />
                                            <MetaRow icon={User} label="OP" value={v.operator || "—"} />
                                        </div>
                                        {v.current_mission && (
                                            <div className="mt-2 pt-2 border-t border-virtus-line font-mono text-[10px] tracking-[0.18em] uppercase text-virtus-orange truncate">
                                                <Target size={10} className="inline mr-1" />
                                                {v.current_mission}
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                )}
            </div>

            <VesselModal
                open={showModal}
                vessel={editing}
                onClose={() => setShowModal(false)}
                onSave={save}
                onDelete={remove}
            />
            <ExpandCard
                open={!!expanded}
                onClose={() => setExpanded(null)}
                onEdit={() => {
                    setEditing(expanded);
                    setShowModal(true);
                    setExpanded(null);
                }}
                code={`VESSEL #${String(expanded?.index || 0).padStart(2, "0")}`}
                ribbonExtras={
                    expanded?.hull_number ? (
                        <>
                            <span className="text-virtus-line">//</span>
                            <div className="font-mono text-xs tracking-[0.24em] uppercase text-virtus-dim">
                                HULL {expanded.hull_number}
                            </div>
                        </>
                    ) : null
                }
                title={expanded?.callsign}
                pills={expanded ? [<StatePill value={expanded.state} />] : []}
                detailBlocks={
                    expanded
                        ? [
                              { icon: Battery, label: "Battery", value: expanded.battery_percent != null ? `${expanded.battery_percent}%` : null, accent: expanded.battery_percent != null && expanded.battery_percent < 20 ? "#FF1744" : null },
                              { icon: Fuel, label: "Fuel", value: expanded.fuel_percent != null ? `${expanded.fuel_percent}%` : null },
                              { icon: Wrench, label: "Hrs run", value: expanded.hours_run != null ? `${expanded.hours_run}` : null },
                              { icon: MapPin, label: "Location", value: expanded.location },
                          ]
                        : []
                }
                sections={
                    expanded
                        ? [
                              { icon: Target, label: "Current mission", value: expanded.current_mission },
                              { icon: User, label: "Operator", value: expanded.operator },
                              { icon: Wrench, label: "Next maintenance", value: expanded.next_maintenance },
                              { icon: Radio, label: "Notes", value: expanded.notes },
                          ]
                        : []
                }
                footerLeft={
                    expanded?.last_checkin
                        ? `LAST CHECK-IN: ${expanded.last_checkin.replace("T", " ").slice(0, 19)}Z`
                        : ""
                }
                testId="fleet-expand-overlay"
                closeTestId="fleet-expand-close-btn"
                editTestId="fleet-expand-edit-btn"
            />
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div className="px-5 py-2 border-r border-virtus-line last:border-r-0 flex items-baseline gap-4">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div className="font-mono font-bold text-2xl" style={{ color }}>
                {String(value).padStart(2, "0")}
            </div>
        </div>
    );
}

function MetaRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-1 truncate">
            <Icon size={10} className="text-virtus-faint flex-shrink-0" />
            <span className="text-virtus-faint">{label}</span>
            <span className="text-white truncate">{value}</span>
        </div>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="border border-dashed border-virtus-line p-16 text-center">
            <Radio size={40} className="mx-auto text-virtus-dim mb-4" />
            <div className="font-heading text-xl uppercase tracking-tight text-white mb-2">
                No vessels registered
            </div>
            <div className="text-sm text-virtus-dim mb-6">
                Add your first autonomous vessel to start tracking status, position, and health.
            </div>
            <button
                onClick={onAdd}
                className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-4 py-2 hover:bg-virtus-orange hover:text-black"
            >
                <Plus size={12} className="inline mr-2" /> ADD FIRST VESSEL
            </button>
        </div>
    );
}

function VesselModal({ open, vessel, onClose, onSave, onDelete }) {
    const [f, setF] = useState(vessel || {});
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

    // Keep form synced when the modal is reopened for a different record.
    if (open && vessel && vessel.id !== f.id) {
        setF(vessel);
    }
    if (open && !vessel && f.id) {
        setF({});
    }

    if (!open) return null;
    const isNew = !vessel?.id;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="w-[560px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        {isNew ? "NEW VESSEL" : "EDIT VESSEL"}
                    </div>
                    <button onClick={onClose} className="text-virtus-dim hover:text-virtus-orange" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                    <Field label="Callsign">
                        <input
                            value={f.callsign || ""}
                            onChange={(e) => set("callsign", e.target.value)}
                            placeholder="Vessel 1"
                            autoFocus
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            data-testid={TID.vesselCallsignInput}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Hull number">
                            <input
                                value={f.hull_number || ""}
                                onChange={(e) => set("hull_number", e.target.value)}
                                placeholder="HN-001"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-sm"
                            />
                        </Field>
                        <Field label="State">
                            <select
                                value={f.state || "on-station"}
                                onChange={(e) => set("state", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {STATE_LIST.map((s) => (
                                    <option key={s} value={s}>
                                        {STATE_META[s].label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Battery %">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={f.battery_percent ?? ""}
                                onChange={(e) => set("battery_percent", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono"
                            />
                        </Field>
                        <Field label="Fuel %">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={f.fuel_percent ?? ""}
                                onChange={(e) => set("fuel_percent", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono"
                            />
                        </Field>
                        <Field label="Hours run">
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={f.hours_run ?? ""}
                                onChange={(e) => set("hours_run", e.target.value === "" ? null : parseFloat(e.target.value))}
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Location (label)">
                            <input
                                value={f.location || ""}
                                onChange={(e) => set("location", e.target.value)}
                                placeholder="Portsmouth, NH"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                            />
                        </Field>
                        <Field label="Operator">
                            <input
                                value={f.operator || ""}
                                onChange={(e) => set("operator", e.target.value)}
                                placeholder="Operator"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Latitude (°)">
                            <input
                                type="number"
                                step="0.0001"
                                min="-90"
                                max="90"
                                value={f.lat ?? ""}
                                onChange={(e) => set("lat", e.target.value === "" ? null : parseFloat(e.target.value))}
                                placeholder="43.0906"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                                data-testid="vessel-lat-input"
                            />
                        </Field>
                        <Field label="Longitude (°)">
                            <input
                                type="number"
                                step="0.0001"
                                min="-180"
                                max="180"
                                value={f.lng ?? ""}
                                onChange={(e) => set("lng", e.target.value === "" ? null : parseFloat(e.target.value))}
                                placeholder="-70.7583"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                                data-testid="vessel-lng-input"
                            />
                        </Field>
                    </div>
                    <Field label="Current mission">
                        <input
                            value={f.current_mission || ""}
                            onChange={(e) => set("current_mission", e.target.value)}
                            placeholder="Mission 1"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                    <Field label="Next maintenance">
                        <input
                            type="date"
                            value={f.next_maintenance || ""}
                            onChange={(e) => set("next_maintenance", e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                        />
                    </Field>
                    <Field label="Notes">
                        <textarea
                            value={f.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={2}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-t border-virtus-line">
                    <div>
                        {!isNew && (
                            <button
                                onClick={onDelete}
                                className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-stuck hover:text-white px-3 py-2 border border-virtus-stuck hover:bg-virtus-stuck"
                                data-testid={TID.vesselDeleteBtn}
                            >
                                <Trash2 size={11} className="inline mr-1" /> DELETE
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={() => {
                                if (!f.callsign?.trim()) return toast.warning("Callsign required.");
                                onSave({
                                    callsign: f.callsign.trim(),
                                    hull_number: f.hull_number || null,
                                    state: f.state || "on-station",
                                    location: f.location || null,
                                    lat: f.lat ?? null,
                                    lng: f.lng ?? null,
                                    battery_percent: f.battery_percent ?? null,
                                    fuel_percent: f.fuel_percent ?? null,
                                    hours_run: f.hours_run ?? null,
                                    next_maintenance: f.next_maintenance || null,
                                    operator: f.operator || null,
                                    current_mission: f.current_mission || null,
                                    notes: f.notes || "",
                                });
                            }}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                            data-testid={TID.vesselSaveBtn}
                        >
                            {isNew ? "CREATE" : "SAVE"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}
